import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  CalendarClock, Download, Mail, ExternalLink, AlertTriangle, FileText,
} from "lucide-react";

function fmtINR(n) {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  if (isNaN(num) || num === 0) return "—";
  return "₹" + num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

function StatTile({ label, value, tone = "slate", testId }) {
  const toneMap = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    pink: "bg-red-50 border-red-200 text-red-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    red: "bg-red-50 border-red-200 text-red-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
  };
  return (
    <div className={`border rounded-lg p-3 ${toneMap[tone]}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-widest opacity-75">{label}</div>
      <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: "Chivo" }}>{value}</div>
    </div>
  );
}

export default function AppraisalDashboard() {
  const today = new Date();
  const currentMonth = today.toISOString().slice(0, 7);
  const [filterMode, setFilterMode] = useState("all"); // all | month | overdue
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState({ rows: [], summary: { total: 0, overdue: 0, due_this_month: 0, due_next_30d: 0 } });
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = filterMode === "month" ? `?month=${month}` : "";
      const [{ data: appraisal }, { data: depts }] = await Promise.all([
        api.get(`/appraisal-dashboard${params}`),
        api.get(`/departments`).catch(() => ({ data: [] })),
      ]);
      setData(appraisal);
      setDepartments(depts || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filterMode, month]);

  const deptMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d.name])), [departments]);

  const visibleRows = useMemo(() => {
    if (filterMode === "overdue") return data.rows.filter((r) => r.is_overdue);
    if (filterMode === "not_set") return data.rows.filter((r) => r.basis === "not_set");
    return data.rows;
  }, [data.rows, filterMode]);

  const downloadCsv = async () => {
    try {
      const params = filterMode === "month" ? `?month=${month}` : "";
      const resp = await api.get(`/appraisal-dashboard/export${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `AppraisalDue_${filterMode === "month" ? month : "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV download failed");
    }
  };

  const sendReminderEmail = async () => {
    if (!window.confirm(`Send appraisal-due reminder email to HR + Senior Mgmt for ${filterMode === "month" ? month : "this month"}?`)) return;
    setSendingEmail(true);
    try {
      const payloadMonth = filterMode === "month" ? month : currentMonth;
      const { data: resp } = await api.post(`/appraisal-dashboard/notify?month=${payloadMonth}`);
      if (resp.sent > 0) {
        toast.success(`Email sent to ${resp.sent} recipient(s) for ${resp.employees} employee(s)`);
      } else {
        toast.message(resp.reason || "No emails sent");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Email send failed");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="appraisal-dashboard">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <CalendarClock className="h-3 w-3" /> HR Tools
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Appraisal Due Dashboard
          </h1>
          <p className="text-xs text-slate-500 mt-0.5">
            Employees grouped by next appraisal due date. Daily auto-email to HR + Senior Mgmt on the 1st of every month.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadCsv} data-testid="export-csv-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
        </Button>
        <Button
          onClick={sendReminderEmail}
          disabled={sendingEmail}
          className="brand-bg brand-bg-hover text-white"
          size="sm"
          data-testid="send-email-btn"
        >
          <Mail className="h-3.5 w-3.5 mr-1.5" /> {sendingEmail ? "Sending…" : "Send Reminder Now"}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile label="Total Employees" value={data.summary.total} tone="slate" testId="stat-total" />
        <StatTile label="Overdue" value={data.summary.overdue} tone="red" testId="stat-overdue" />
        <StatTile label="Due This Month" value={data.summary.due_this_month} tone="pink" testId="stat-due-month" />
        <StatTile label="Due in 30 Days" value={data.summary.due_next_30d} tone="amber" testId="stat-due-30" />
        <StatTile label="Date Not Set" value={data.summary.not_set || 0} tone="slate" testId="stat-not-set" />
      </div>

      {/* Filter strip */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-500">Filter</Label>
          <Select value={filterMode} onValueChange={setFilterMode}>
            <SelectTrigger className="w-44 mt-1" data-testid="filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All employees</SelectItem>
              <SelectItem value="month">By month</SelectItem>
              <SelectItem value="overdue">Overdue only</SelectItem>
              <SelectItem value="not_set">Date not set</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {filterMode === "month" && (
          <div>
            <Label className="text-[10px] uppercase tracking-widest text-slate-500">Month</Label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="mt-1 w-44"
              data-testid="month-input"
            />
          </div>
        )}
        <div className="flex-1 text-xs text-slate-500 text-right">
          Showing <b>{visibleRows.length}</b> employee(s)
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading…</div>
        ) : visibleRows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">
            <CalendarClock className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            No employees with appraisal due in this filter.
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="appraisal-table">
            <thead className="bg-slate-100">
              <tr className="text-left text-slate-600">
                <th className="px-3 py-2 font-semibold">Punch ID</th>
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Designation</th>
                <th className="px-3 py-2 font-semibold">Department</th>
                <th className="px-3 py-2 font-semibold text-right">Current CTC</th>
                <th className="px-3 py-2 font-semibold">Last Revision</th>
                <th className="px-3 py-2 font-semibold">Next Due</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold w-12"></th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => (
                <tr
                  key={r.employee_id}
                  className={`border-t border-slate-100 hover:bg-slate-50/60 ${r.is_overdue ? "bg-red-50/30" : ""}`}
                  data-testid={`appraisal-row-${r.punch_id}`}
                >
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{r.punch_id || "—"}</td>
                  <td className="px-3 py-2 font-semibold text-slate-900">{r.name}</td>
                  <td className="px-3 py-2 text-slate-600">{r.designation || "—"}</td>
                  <td className="px-3 py-2 text-slate-600">{deptMap[r.department_id] || "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">{fmtINR(r.ctc_current)}</td>
                  <td className="px-3 py-2 text-slate-600">
                    {r.last_revision_date || <span className="italic text-slate-400">No revision yet</span>}
                    {r.last_revision_number !== null && r.last_revision_number !== undefined && (
                      <span className="ml-1 text-[10px] text-slate-400">(Rev {String(r.last_revision_number).padStart(2, "0")})</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-semibold">
                    {r.next_appraisal_due_date || <span className="italic text-slate-400 font-normal">Not set</span>}
                  </td>
                  <td className="px-3 py-2">
                    {r.basis === "not_set" ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
                        DOJ missing
                      </span>
                    ) : r.is_overdue ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold">
                        <AlertTriangle className="h-3 w-3" /> OVERDUE {Math.abs(r.days_remaining)}d
                      </span>
                    ) : r.days_remaining <= 30 ? (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-bold">
                        Due in {r.days_remaining}d
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold">
                        {r.days_remaining}d left
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      data-testid={`open-record-${r.punch_id}`}
                      title="Open Master Record"
                    >
                      <Link to={`/employees/${r.employee_id}/master-record`}>
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Info footer */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex items-start gap-2">
        <FileText className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
        <div>
          <b>How dates are computed:</b> If the latest Master Record has a "Next Appraisal Due Date" set, that's used.
          Otherwise we default to <b>last revision date + 12 months</b>, or <b>date of joining + 12 months</b> if no revision exists.
          Set the next due date explicitly while creating a revision for full control.
        </div>
      </div>
    </div>
  );
}
